import { KnowledgeItem, LearningCurveData, RetentionRate, SkillDistribution, WeakArea, TacticalThemeProficiency } from '../../types/learning';
import { isSameDay, format } from 'date-fns';

/**
 * Calculates the learning curve data for a set of knowledge items.
 * @param items Array of KnowledgeItem
 * @returns Array of LearningCurveData, one per date
 */
export function calculateLearningCurve(items: KnowledgeItem[]): LearningCurveData[] {
  // Map of date string to { reviewed, mastered }
  const curveMap: Record<string, { reviewed: number; mastered: number }> = {};
  const MASTERED_INTERVAL = 21; // days, threshold for "mastered"

  items.forEach(item => {
    if (!item.lastReviewed) return;
    const dateStr = format(item.lastReviewed, 'yyyy-MM-dd');
    if (!curveMap[dateStr]) {
      curveMap[dateStr] = { reviewed: 0, mastered: 0 };
    }
    curveMap[dateStr].reviewed += 1;
    if (item.interval >= MASTERED_INTERVAL) {
      curveMap[dateStr].mastered += 1;
    }
  });

  return Object.entries(curveMap).map(([date, { reviewed, mastered }]) => ({
    date,
    reviewed,
    mastered,
  }));
}

/**
 * Calculates the learning curve data for a specific type of knowledge items.
 * @param items Array of KnowledgeItem
 * @param itemType The type of knowledge item to filter by (e.g., 'sequence', 'move')
 * @returns Array of LearningCurveData, one per date, for the specified item type
 */
export function calculateTypedLearningCurve(items: KnowledgeItem[], itemType: string): LearningCurveData[] {
  // Filter items by the specified type first
  const typedItems = items.filter(item => item.type === itemType);

  // Map of date string to { reviewed, mastered }
  const curveMap: Record<string, { reviewed: number; mastered: number }> = {};
  const MASTERED_INTERVAL = 21; // days, threshold for "mastered"

  typedItems.forEach(item => {
    if (!item.lastReviewed) return;
    const dateStr = format(item.lastReviewed, 'yyyy-MM-dd');
    if (!curveMap[dateStr]) {
      curveMap[dateStr] = { reviewed: 0, mastered: 0 };
    }
    curveMap[dateStr].reviewed += 1;
    if (item.interval >= MASTERED_INTERVAL) {
      curveMap[dateStr].mastered += 1;
    }
  });

  return Object.entries(curveMap).map(([date, { reviewed, mastered }]) => ({
    date,
    reviewed,
    mastered,
  }));
}

/**
 * Calculates the retention rate for a set of knowledge items.
 * @param items Array of KnowledgeItem
 * @returns RetentionRate object
 */
export function calculateRetentionRate(items: KnowledgeItem[]): RetentionRate {
  let totalReviews = 0;
  let correctReviews = 0;
  items.forEach(item => {
    item.performanceHistory.forEach(grade => {
      totalReviews += 1;
      if (grade >= 4) correctReviews += 1;
    });
  });
  const percentRetained = totalReviews === 0 ? 0 : Math.round((correctReviews / totalReviews) * 100);
  return { percentRetained, totalReviews, correctReviews };
}

/**
 * Calculates skill distribution across different categories based on knowledge items.
 * Returns normalized proficiency scores (0-100) for each skill category.
 * @param items Array of KnowledgeItem
 * @returns SkillDistribution object mapping categories to proficiency scores
 */
export function calculateSkillDistribution(items: KnowledgeItem[]): SkillDistribution {
  if (!items || items.length === 0) {
    return {};
  }

  // Group items by type and calculate average performance
  const typesMap: Record<string, { totalScore: number, count: number }> = {};
  
  items.forEach(item => {
    // Initialize if first time seeing this type
    if (!typesMap[item.type]) {
      typesMap[item.type] = { totalScore: 0, count: 0 };
    }
    
    // Skip items with no performance history
    if (item.performanceHistory.length === 0) return;
    
    // Calculate average grade (0-5 scale from SM-2)
    const avgGrade = item.performanceHistory.reduce((sum, grade) => sum + grade, 0) / 
                      item.performanceHistory.length;
    
    // Convert to 0-100 scale
    const score = Math.round((avgGrade / 5) * 100);
    
    typesMap[item.type].totalScore += score;
    typesMap[item.type].count += 1;
  });
  
  // Calculate averages and create final distribution
  const distribution: SkillDistribution = {};
  
  Object.entries(typesMap).forEach(([type, { totalScore, count }]) => {
    if (count > 0) {
      distribution[type] = Math.round(totalScore / count);
    } else {
      distribution[type] = 0;
    }
  });
  
  return distribution;
}

/**
 * Identifies skill areas needing improvement based on performance metrics.
 * Returns categories sorted by weakest performance first.
 * @param items Array of KnowledgeItem
 * @returns Array of WeakAreas, sorted by performance (ascending)
 */
export function identifyWeakAreas(items: KnowledgeItem[]): WeakArea[] {
  if (!items || items.length === 0) {
    return [];
  }
  
  // Get current skill distribution
  const distribution = calculateSkillDistribution(items);
  
  // Convert to array of WeakArea objects
  const weakAreas: WeakArea[] = Object.entries(distribution).map(([category, performance]) => ({
    category,
    performance
  }));
  
  // Sort by performance (ascending - weakest first)
  weakAreas.sort((a, b) => a.performance - b.performance);
  
  // Filter to only return categories below target threshold (70%)
  const TARGET_THRESHOLD = 70;
  return weakAreas.filter(area => area.performance < TARGET_THRESHOLD);
}

/**
 * Analyzes tactical theme understanding based on sequence items.
 * Extracts tactical themes from sequence items and calculates proficiency for each.
 * @param items Array of KnowledgeItem, typically filtered to sequence type
 * @returns Record mapping tactical themes to proficiency scores (0-100)
 */
export function analyzeTacticalThemeProficiency(items: KnowledgeItem[]): TacticalThemeProficiency {
  if (!items || items.length === 0) {
    return {};
  }
  
  // Filter for sequence items only, as these are the ones with tactical themes
  const sequenceItems = items.filter(item => item.type === 'sequence');
  
  if (sequenceItems.length === 0) {
    return {};
  }
  
  // Extract tactical themes from content or tags
  // Note: This implementation assumes tactical themes are stored in the content field
  // in a format like "fork", "pin", "discovered attack", etc.
  // A more robust implementation would parse structured data from item.content
  const themeScores: Record<string, { totalScore: number, count: number }> = {};
  
  sequenceItems.forEach(item => {
    // Skip items with no performance history
    if (item.performanceHistory.length === 0) return;
    
    // Extract theme - this is a simplified approach and may need adjustment based on actual data structure
    // Assuming content has format like "fork: e4 to g5" or contains theme as a keyword
    const potentialThemes = ['fork', 'pin', 'discovered attack', 'sacrifice', 'checkmate'];
    let theme = 'general';
    
    for (const t of potentialThemes) {
      if (item.content.toLowerCase().includes(t.toLowerCase())) {
        theme = t;
        break;
      }
    }
    
    // Initialize if first time seeing this theme
    if (!themeScores[theme]) {
      themeScores[theme] = { totalScore: 0, count: 0 };
    }
    
    // Calculate average grade (0-5 scale)
    const avgGrade = item.performanceHistory.reduce((sum, grade) => sum + grade, 0) / 
                      item.performanceHistory.length;
    
    // Convert to 0-100 scale
    const score = Math.round((avgGrade / 5) * 100);
    
    themeScores[theme].totalScore += score;
    themeScores[theme].count += 1;
  });
  
  // Calculate averages for each theme
  const themeProficiency: TacticalThemeProficiency = {};
  
  Object.entries(themeScores).forEach(([theme, { totalScore, count }]) => {
    if (count > 0) {
      themeProficiency[theme] = Math.round(totalScore / count);
    } else {
      themeProficiency[theme] = 0;
    }
  });
  
  return themeProficiency;
}

/**
 * Aggregates review grade counts by type for grouped histogram.
 * @param items Array of KnowledgeItem
 * @returns Array of objects: { type, grade, count }
 */
export function getGradeHistogramByType(items: KnowledgeItem[]): { type: string; grade: number; count: number }[] {
  const result: Record<string, Record<number, number>> = {};
  items.forEach(item => {
    if (!result[item.type]) result[item.type] = {};
    item.performanceHistory.forEach(grade => {
      result[item.type][grade] = (result[item.type][grade] || 0) + 1;
    });
  });
  // Flatten to array
  const out: { type: string; grade: number; count: number }[] = [];
  Object.entries(result).forEach(([type, grades]) => {
    for (let grade = 0; grade <= 5; grade++) {
      out.push({ type, grade, count: grades[grade] || 0 });
    }
  });
  return out;
} 